import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';
import {
  XMarkIcon,
  EnvelopeIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { apiService } from '../../services/api';
import { trackEvent } from '../../utils/analytics';

export const SUPPORT_EMAIL = 'support@clubcoachapp.nl';

interface SupportModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Voorgevulde tekst wanneer support vanuit een concreet probleem wordt geopend
   * (bijvoorbeeld een teamnaam die al bezet is). Alleen de beginwaarde: zodra de
   * gebruiker typt is het bericht van hem of haar. Mount de modal daarom pas als
   * hij open moet, anders blijft de eerste beginwaarde plakken.
   */
  initieelOnderwerp?: string;
  initieelBericht?: string;
}

/**
 * Supportbericht vanuit de app. Verstuurt via de backend (Mailgun), niet via mailto -
 * dan hoeft de gebruiker geen werkende mailclient te hebben, wat op mobiel vaak misgaat.
 *
 * Naam, e-mail en team hoeven we niet te vragen: die kent de server al uit het token.
 * Bij een fout tonen we het directe adres, zodat een vraag nooit doodloopt.
 */
export const SupportModal: React.FC<SupportModalProps> = ({
  isOpen,
  onClose,
  initieelOnderwerp = '',
  initieelBericht = '',
}) => {
  const { t } = useTranslation(['settings', 'common']);
  const { currentTeam } = useAuth();
  const [onderwerp, setOnderwerp] = useState(initieelOnderwerp);
  const [bericht, setBericht] = useState(initieelBericht);
  const [status, setStatus] = useState<'invoer' | 'versturen' | 'verzonden' | 'fout'>('invoer');
  const [foutmelding, setFoutmelding] = useState('');
  // Bij een invoerfout is rechtstreeks mailen geen zinnige uitwijk - het bericht is
  // dan gewoon nog niet af. Alleen bij een mislukte verzending tonen we dat adres.
  const [toonMailUitwijk, setToonMailUitwijk] = useState(false);

  if (!isOpen) return null;

  const sluitEnReset = () => {
    setOnderwerp(initieelOnderwerp);
    setBericht(initieelBericht);
    setStatus('invoer');
    setFoutmelding('');
    setToonMailUitwijk(false);
    onClose();
  };

  const handleVerstuur = async () => {
    if (status === 'versturen') return;

    // Bewust geen uitgeschakelde knop: die laat de gebruiker raden wat er mis is.
    if (bericht.trim().length < 5) {
      setStatus('fout');
      setToonMailUitwijk(false);
      setFoutmelding(t('support.tooShort'));
      return;
    }

    setStatus('versturen');
    setFoutmelding('');

    try {
      await apiService.sendSupportMessage({
        bericht: bericht.trim(),
        onderwerp: onderwerp.trim() || undefined,
        pagina: window.location.pathname,
      });

      trackEvent('Support Bericht', { bron: 'app' });
      setStatus('verzonden');
    } catch (error) {
      setStatus('fout');
      setToonMailUitwijk(true);
      setFoutmelding(
        error instanceof Error && error.message.includes('429')
          ? t('support.rateLimited')
          : t('support.sendFailed')
      );
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
      onClick={sluitEnReset}
    >
      <div
        className="w-full max-w-lg rounded-xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b border-gray-200 p-6">
          <div className="flex items-center">
            <div className="mr-4 flex h-10 w-10 items-center justify-center rounded-lg bg-hockey-blue">
              <EnvelopeIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-hockey-brown">{t('support.heading')}</h2>
              <p className="text-sm text-gray-600">
                {t('support.intro')}
              </p>
            </div>
          </div>
          <button
            onClick={sluitEnReset}
            className="text-gray-400 transition-colors hover:text-gray-600"
            aria-label={t('common:actions.close')}
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {status === 'verzonden' ? (
          <div className="p-8 text-center">
            <CheckCircleIcon className="mx-auto mb-4 h-12 w-12 text-green-500" />
            <h3 className="mb-2 text-lg font-bold text-hockey-brown">{t('support.sentTitle')}</h3>
            <p className="mb-6 text-gray-600">
              {t('support.sent')}
            </p>
            <button
              onClick={sluitEnReset}
              className="rounded-lg bg-hockey-blue px-6 py-2 font-medium text-white transition-colors hover:bg-blue-400"
            >
              {t('common:actions.close')}
            </button>
          </div>
        ) : (
          <>
            <div className="space-y-4 p-6">
              <div>
                <label
                  htmlFor="support-onderwerp"
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  {t('support.subjectLabel')}{' '}
                  <span className="font-normal text-gray-400">{t('support.optional')}</span>
                </label>
                <input
                  id="support-onderwerp"
                  type="text"
                  value={onderwerp}
                  onChange={(e) => setOnderwerp(e.target.value)}
                  maxLength={150}
                  placeholder={t('support.subjectPlaceholder')}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-hockey-blue focus:outline-none focus:ring-1 focus:ring-hockey-blue"
                />
              </div>

              <div>
                <label
                  htmlFor="support-bericht"
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  {t('support.messageLabel')}
                </label>
                <textarea
                  id="support-bericht"
                  value={bericht}
                  onChange={(e) => setBericht(e.target.value)}
                  rows={5}
                  maxLength={5000}
                  placeholder={t('support.messagePlaceholder')}
                  className="w-full resize-y rounded-lg border border-gray-300 px-3 py-2 focus:border-hockey-blue focus:outline-none focus:ring-1 focus:ring-hockey-blue"
                />
              </div>

              {status === 'fout' && (
                <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">
                  {foutmelding}
                  {toonMailUitwijk && (
                    <>
                      {' '}{t('support.mailFallback')}{' '}
                      <a href={`mailto:${SUPPORT_EMAIL}`} className="font-medium underline">
                        {SUPPORT_EMAIL}
                      </a>
                      .
                    </>
                  )}
                </div>
              )}

              <p className="text-xs text-gray-500">
                {currentTeam ? t('support.privacyWithTeam') : t('support.privacy')}
              </p>

              <button
                onClick={handleVerstuur}
                disabled={status === 'versturen'}
                className="flex w-full items-center justify-center rounded-lg bg-hockey-blue px-4 py-3 font-medium text-white transition-colors hover:bg-blue-400 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400"
              >
                <PaperAirplaneIcon className="mr-2 h-5 w-5" />
                {status === 'versturen' ? t('support.sending') : t('support.submit')}
              </button>
            </div>

            <div className="rounded-b-xl border-t border-gray-200 bg-gray-50 px-6 py-4">
              <p className="text-sm text-gray-600">
                {t('support.directMail')}{' '}
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="font-medium text-hockey-blue hover:underline"
                  onClick={() => trackEvent('Support Bericht', { bron: 'app-direct' })}
                >
                  {SUPPORT_EMAIL}
                </a>
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SupportModal;
